import React, { useState, useMemo, useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '../../WebApp/Controllers/hooks';
import '../Css/custom.css';

import { motion, AnimatePresence } from 'framer-motion';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Tooltip } from 'primereact/tooltip';
import { Ripple } from 'primereact/ripple';
import { Fade } from 'react-awesome-reveal';

import { fetchUseCaseModules, fetchUseCaseUserTypes } from '../../WebApp/Controllers/Thunks/WebsiteThunks';

const ModuleBreakdown: React.FC = () => {
    const dispatch = useAppDispatch();

    useEffect(() => {
        dispatch(fetchUseCaseModules());
        dispatch(fetchUseCaseUserTypes());
    }, [dispatch]);

    const {
        loadingUseCaseUserTypes, errorUseCaseUserTypes, useCaseUserTypes,
        loadingUseCaseModules, errorUseCaseModules, useCaseModules
    } = useAppSelector(state => state.website);

    useEffect(() => {
        console.log(useCaseModules);
        console.log(useCaseUserTypes);
    }, [useCaseModules, useCaseUserTypes]);

    const [expandedModules, setExpandedModules] = useState<Record<number, boolean>>({});
    const [isolatedUserTypeId, setIsolatedUserTypeId] = useState<number | null>(null);

    useEffect(() => {
        if (useCaseModules.length > 0) {
            setExpandedModules(prev => {
                if (Object.keys(prev).length === 0) {
                    const initial: Record<number, boolean> = {};
                    useCaseModules.forEach(m => {
                        initial[m.id] = true;
                    });
                    return initial;
                }
                return prev;
            });
        }
    }, [useCaseModules]);

    const activeUserTypes = useMemo(() => {
        return useCaseUserTypes.filter(ut => ut.status === 1);
    }, [useCaseUserTypes]);

    const activeModules = useMemo(() => {
        return useCaseModules.filter(m => m.status === 1)?.sort((a, b) => a.order_no - b.order_no);
    }, [useCaseModules]);

    const toggleModule = (moduleId: number) => {
        const willExpand = !expandedModules[moduleId];
        setExpandedModules(prev => ({
            ...prev,
            [moduleId]: willExpand
        }));

        if (willExpand) {
            setTimeout(() => {
                const moduleRow = document.getElementById(`module-row-${moduleId}`);
                const wrapper = document.querySelector('.mbd-table-wrapper');

                if (moduleRow && wrapper) {
                    const rowRect = moduleRow.getBoundingClientRect();
                    const wrapperRect = wrapper.getBoundingClientRect();
                    const headerHeight = 165;

                    wrapper.scrollBy({
                        top: rowRect.top - wrapperRect.top - headerHeight,
                        behavior: 'smooth'
                    });
                }
            }, 300);
        }
    };

    const handleIsolateToggle = (userTypeId: number) => {
        setIsolatedUserTypeId(prev => prev === userTypeId ? null : userTypeId);
    };

    const displayUserTypes = useMemo(() => {
        if (isolatedUserTypeId !== null) {
            return activeUserTypes.filter(ut => ut.id === isolatedUserTypeId);
        }
        return activeUserTypes;
    }, [activeUserTypes, isolatedUserTypeId]);

    return (
        <div className="container-fluid pb-4 mbd-container">
            <Fade triggerOnce duration={300}>
                {(loadingUseCaseModules || loadingUseCaseUserTypes) ? (
                    <div className="mbd-loading-area">
                        <ProgressSpinner
                            className="progresscircle custom"
                            style={{ width: '50px', height: '50px', stroke: 'var(--primary-color)' }}
                            strokeWidth="8"
                            fill="#FFF"
                            animationDuration="1s"
                        />
                    </div>
                ) : (errorUseCaseModules || errorUseCaseUserTypes) ? (
                    <div className="mbd-error-area">
                        <div className="text-center">
                            <i className="bi bi-exclamation-circle-fill text-danger fs-1 mb-4"></i>
                            <h5>Error Fetching Data.</h5>
                            <p>Failed to retrieve use case details. Please try again later.</p>
                        </div>
                    </div>
                ) : (
                    <div className="mbd-table-wrapper">
                        <table className="mbd-table table table-borderless mb-0">
                            <thead className="mbd-thead">
                                <tr>
                                    <th className="mbd-sticky-col mbd-sticky-corner">
                                        {/* Module / Feature */}
                                    </th>
                                    {displayUserTypes.map(ut => (
                                        <th key={ut.id} className="text-center mbd-header-cell">
                                            <div className="mbd-header-cell-item">
                                                <Tooltip target={`#mbd-toggle-switch-${ut.id}`} position='left' content="Isolate" />
                                                {/* Isolated mode toggle */}
                                                <label className="mbd-toggle-switch" id={`mbd-toggle-switch-${ut.id}`} data-pr-tooltip="Isolate">
                                                    <input
                                                        type="checkbox"
                                                        checked={isolatedUserTypeId === ut.id}
                                                        onChange={() => handleIsolateToggle(ut.id)}
                                                    />
                                                    <span className="mbd-toggle-slider"></span>
                                                </label>
                                                {ut.logo && (
                                                    <img src={ut.logo} alt={ut.type_name} className="mbd-user-logo mb-2" />
                                                )}
                                                <span className="mbd-user-type-name">
                                                    {ut.type_name}
                                                </span>
                                            </div>
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody className="mbd-tbody">
                                {activeModules.map(module => {
                                    const isExpanded = !!expandedModules[module.id];
                                    const activeFeatures = module.module_feature.filter(f => f.status === 1)?.sort((a, b) => a.order_no - b.order_no);

                                    return (
                                        <React.Fragment key={module.id}>
                                            {/* Module Row */}
                                            <tr
                                                id={`module-row-${module.id}`}
                                                className="mbd-module-row"
                                                onClick={() => toggleModule(module.id)}
                                                style={{ cursor: 'pointer' }}
                                            >
                                                <td className="mbd-sticky-col mbd-module-cell overflow-hidden">
                                                    <div className="d-flex align-items-center">
                                                        <i className={`bi bi-chevron-right ${isExpanded ? 'expanded' : ''} mbd-expand-icon`}></i>
                                                        <span className="ms-2 fw-semibold mbd-module-name">{module.module}</span>
                                                        <Tooltip target={`#mod-info-${module.id}`} position="right" />
                                                        <i
                                                            id={`mod-info-${module.id}`}
                                                            className="bi bi-info-circle ms-2 mbd-info-icon"
                                                            data-pr-tooltip={module.long_description}
                                                        ></i>
                                                    </div>
                                                    <Ripple />
                                                </td>
                                                {displayUserTypes.map(ut => (
                                                    <td key={ut.id} className="mbd-empty-cell"></td>
                                                ))}
                                            </tr>

                                            {/* Feature Rows */}
                                            <AnimatePresence initial={false}>
                                                {isExpanded && activeFeatures.map(feature => (
                                                    <tr
                                                        key={`${module.id}-${feature.id}`}
                                                        className="mbd-feature-row"
                                                    >
                                                        <td className="mbd-sticky-col mbd-feature-cell p-0 border-0">
                                                            <motion.div
                                                                className="d-flex align-items-center mbd-feature-indent w-100 h-100"
                                                                initial={{ height: 0, opacity: 0 }}
                                                                animate={{ height: "auto", opacity: 1 }}
                                                                exit={{ height: 0, opacity: 0 }}
                                                                transition={{ duration: 0.3, ease: 'easeInOut' }}
                                                            >
                                                                <span className="mbd-feature-name">{feature.feature}</span>
                                                                <Tooltip target={`#feat-info-${feature.id}`} position="right" />
                                                                <i
                                                                    id={`feat-info-${feature.id}`}
                                                                    className="bi bi-info-circle ms-2 mbd-info-icon mbd-feature-info-icon"
                                                                    data-pr-tooltip={feature.long_description}
                                                                ></i>
                                                            </motion.div>
                                                        </td>
                                                        {displayUserTypes.map(ut => {
                                                            const isAvailable = feature.available_for.includes(ut.id);
                                                            return (
                                                                <td key={ut.id} className="text-center align-middle p-0 border-0">
                                                                    <motion.div
                                                                        className="d-flex justify-content-center align-items-center h-100 mbd-icon-cell"
                                                                        initial={{ height: 0, opacity: 0 }}
                                                                        animate={{ height: "auto", opacity: 1 }}
                                                                        exit={{ height: 0, opacity: 0 }}
                                                                        transition={{ duration: 0.3, ease: 'easeOut' }}
                                                                    >
                                                                        {isAvailable ? (
                                                                            <div
                                                                                className="mbd-badge mbd-badge--yes"
                                                                                aria-label={`Available for ${ut.type_name}`}
                                                                            >
                                                                                <i className="bi bi-check-lg"></i>
                                                                            </div>
                                                                        ) : (
                                                                            <div
                                                                                className="mbd-badge mbd-badge--no"
                                                                                aria-label={`Not available for ${ut.type_name}`}
                                                                            >
                                                                                <i className="bi bi-x-lg"></i>
                                                                            </div>
                                                                        )}
                                                                    </motion.div>
                                                                </td>
                                                            );
                                                        })}
                                                    </tr>
                                                ))}
                                            </AnimatePresence>
                                        </React.Fragment>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </Fade>
        </div>
    );
};

export default ModuleBreakdown;